import { useCallback, useEffect, useState } from 'react';

type UseZplConfig = {
  dpmm?: number;
  size?: string;
};

type UseZplResult = {
  zpl: string | null;
  renderZpl: (value: string | null) => void;
  imageUrl: string | null;
  loading: boolean;
  error: string | null;
};

export function useZpl(initialZplTemplate: string | null = null, config: UseZplConfig = {}): UseZplResult {
  const [zpl, setZpl] = useState<string | null>(initialZplTemplate);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const { dpmm = 12, size = '2x9' } = config;

  useEffect(() => {
    // Clean up any created object URLs when the component unmounts or when imageUrl changes
    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  useEffect(() => {
    async function renderZpl() {
      if (!zpl) {
        setImageUrl(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await fetch(`https://api.labelary.com/v1/printers/${dpmm}dpmm/labels/${size}/0/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Accept': 'image/png',
            'X-Rotation': '90',
          },
          body: zpl,
        });

        if (!response.ok) {
          const errorText = await response.text();
          setError(`Failed to render label: ${errorText}`);
          setImageUrl(null);

          return;
        }

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);

        setImageUrl(url);
      } catch (err) {
        setError(`Error rendering ZPL: ${err instanceof Error ? err.message : String(err)}`);
        setImageUrl(null);
      } finally {
        setLoading(false);
      }
    }

    renderZpl();
  }, [zpl, dpmm, size]);

  return { zpl, renderZpl: setZpl, imageUrl, loading, error };
}
